/**
 * Lado da interface: conversa com o Worker do núcleo. `api` tem o MESMO formato
 * `Api` do desktop/web (packages/core/shared/types.ts), então as telas chamam
 * `api.clientes.list()` igual ao app atual — só o transporte mudou (ADR-002).
 * Também expõe o estado da sincronização (ADR-003) para a casca.
 */
import { createSignal } from 'solid-js'
import type { Api } from '@shared/types'
import type { InfoBanco, EstadoSync } from './worker'

export class ErroNucleo extends Error {
  constructor(
    nome: string,
    mensagem: string,
    readonly issues?: unknown,
  ) {
    super(mensagem)
    this.name = nome
  }
}

type Resposta = { id: number; ok: true; dado: unknown } | { id: number; ok: false; erro: { name: string; message: string; issues?: unknown } }
type Evento = { evento: 'aguardando-outra-aba' } | { evento: 'dados-mudaram' } | { evento: 'sync'; estado: EstadoSync }

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'werc-nucleo' })
let seq = 0
const pendentes = new Map<number, { resolve: (v: unknown) => void; reject: (e: unknown) => void }>()

const ouvintes = new Set<(e: 'aguardando-outra-aba') => void>()
/** Eventos de abertura (ex.: esperando outra aba soltar o banco). */
export function aoEventoNucleo(fn: (e: 'aguardando-outra-aba') => void): () => void {
  ouvintes.add(fn)
  return () => ouvintes.delete(fn)
}

/** Estado da sincronização (online, pendências, recusas) para a casca mostrar. */
const [estadoSync, setEstadoSync] = createSignal<EstadoSync | null>(null)
export { estadoSync }
/** Sobe quando chega novidade do servidor: telas que dependem dele recarregam. */
const [versaoDados, setVersaoDados] = createSignal(0)
export { versaoDados }

worker.onmessage = (ev: MessageEvent<Resposta | Evento>) => {
  if ('evento' in ev.data) {
    const e = ev.data
    if (e.evento === 'sync') setEstadoSync(e.estado)
    else if (e.evento === 'dados-mudaram') setVersaoDados((v) => v + 1)
    else for (const fn of ouvintes) fn(e.evento)
    return
  }
  const r = ev.data
  const p = pendentes.get(r.id)
  if (!p) return
  pendentes.delete(r.id)
  if (r.ok) p.resolve(r.dado)
  else p.reject(new ErroNucleo(r.erro.name, r.erro.message, r.erro.issues))
}
worker.onerror = (ev) => {
  const e = new ErroNucleo('WorkerError', ev.message || 'Falha ao carregar o núcleo do app.')
  for (const p of pendentes.values()) p.reject(e)
  pendentes.clear()
}

function enviarTransferindo<T>(msg: Record<string, unknown>, transferir: Transferable[]): Promise<T> {
  const id = ++seq
  return new Promise<T>((resolve, reject) => {
    pendentes.set(id, { resolve: resolve as (v: unknown) => void, reject })
    worker.postMessage({ id, ...msg }, transferir)
  })
}

function enviar<T>(msg: Record<string, unknown>): Promise<T> {
  const id = ++seq
  return new Promise<T>((resolve, reject) => {
    pendentes.set(id, { resolve: resolve as (v: unknown) => void, reject })
    worker.postMessage({ id, ...msg })
  })
}

/** Abre o banco local (persistente) e aplica as migrações. Chamar uma vez na abertura. */
export function iniciarNucleo(): Promise<InfoBanco> {
  return enviar<InfoBanco>({ tipo: 'iniciar' })
}

export function chamar<T = unknown>(area: string, metodo: string, args: unknown[]): Promise<T> {
  return enviar<T>({ tipo: 'rpc', area, metodo, args })
}

/** `api.<area>.<metodo>(...args)` → Worker. Tipado pelo contrato `Api`. */
export const api = new Proxy({} as Api, {
  get: (_alvo, area: string) =>
    new Proxy(
      {},
      {
        get: (_a, metodo: string) =>
          (...args: unknown[]) =>
            chamar(area, metodo, args),
      },
    ),
}) as Api

/** Anexos do chat (F4): o arquivo vai/vem pela API (precisa de internet; o que já foi aberto fica em cache). */
export const anexos = {
  async enviar(arquivo: File): Promise<{ chave: string; nome: string; mime: string; tamanho: number }> {
    const bytes = await arquivo.arrayBuffer()
    return enviarTransferindo({ tipo: 'anexoEnviar', bytes, nome: arquivo.name, mime: arquivo.type || 'application/octet-stream' }, [bytes])
  },
  baixar: (chave: string) => enviar<{ bytes: ArrayBuffer; mime: string }>({ tipo: 'anexoBaixar', chave }),
}

export const sync = {
  agora: () => chamar<EstadoSync>('sync', 'agora', []),
  estado: () => chamar<EstadoSync>('sync', 'estado', []),
  dispensarRecusados: () => chamar<null>('sync', 'dispensarRecusados', []),
}

/** Pede ao navegador para não apagar o banco local sob pouco espaço em disco. */
export async function pedirPersistencia(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false
  } catch {
    return false
  }
}
